/**
 * Event Manager - Centralized Event Delegation System
 *
 * Replaces inline onclick/onchange handlers with a single delegated event system.
 * Uses data-action attributes to map events to handlers.
 *
 * @example
 * HTML: <button data-action="navigate" data-target="screen-welcome">Back</button>
 *
 * JS: eventManager.on('click', 'navigate', (e, el) => {
 *   const target = el.dataset.target;
 *   goToScreen(target);
 * });
 */

export type EventHandler = (event: Event, element: HTMLElement) => void | Promise<void>;

export type SupportedEventType = 'click' | 'change' | 'submit' | 'keydown' | 'keyup' | 'input';

export interface EventManagerConfig {
  /** Root element for event delegation. Defaults to document.body */
  root?: HTMLElement;
  /** Enable debug logging */
  debug?: boolean;
  /** Prevent default on all handled events */
  preventDefault?: boolean;
  /** Stop propagation on all handled events */
  stopPropagation?: boolean;
}

interface HandlerEntry {
  handler: EventHandler;
  options?: {
    preventDefault?: boolean;
    stopPropagation?: boolean;
    once?: boolean;
  };
}

/**
 * Centralized event manager using event delegation pattern.
 *
 * Benefits over inline handlers:
 * - Single event listener per event type (better performance)
 * - Easy to add/remove handlers dynamically
 * - Testable - handlers can be mocked
 * - Works with dynamically added elements
 * - Separation of concerns - HTML stays clean
 */
export class EventManager {
  private root: HTMLElement;
  private handlers: Map<SupportedEventType, Map<string, HandlerEntry>>;
  private boundHandleEvent: (event: Event) => void;
  private debug: boolean;
  private globalPreventDefault: boolean;
  private globalStopPropagation: boolean;
  private attachedEvents: Set<SupportedEventType>;

  constructor(config: EventManagerConfig = {}) {
    this.root = config.root ?? document.body;
    this.handlers = new Map();
    this.boundHandleEvent = this.handleEvent.bind(this);
    this.debug = config.debug ?? false;
    this.globalPreventDefault = config.preventDefault ?? false;
    this.globalStopPropagation = config.stopPropagation ?? false;
    this.attachedEvents = new Set();
  }

  /**
   * Initialize event delegation for common event types.
   * Call this after DOM is ready.
   */
  init(): void {
    this.attachEventType('click');
    this.attachEventType('change');
    this.attachEventType('submit');
    this.attachEventType('keydown');

    if (this.debug) {
      console.log('[EventManager] Initialized with root:', this.root);
    }
  }

  /**
   * Attach delegation for a specific event type.
   */
  private attachEventType(eventType: SupportedEventType): void {
    if (this.attachedEvents.has(eventType)) return;

    this.root.addEventListener(eventType, this.boundHandleEvent, true);
    this.attachedEvents.add(eventType);

    if (this.debug) {
      console.log(`[EventManager] Attached ${eventType} listener`);
    }
  }

  /**
   * Handle delegated events by finding the action and calling the registered handler.
   */
  private handleEvent(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target) return;

    // Find the closest element with data-action attribute
    const actionElement = target.closest('[data-action]') as HTMLElement | null;
    if (!actionElement) return;

    const action = actionElement.dataset.action;
    if (!action) return;

    const eventType = event.type as SupportedEventType;
    const handlerEntry = this.handlers.get(eventType)?.get(action);

    if (!handlerEntry) {
      if (this.debug) {
        console.log(`[EventManager] No handler for ${eventType}:${action}`);
      }
      return;
    }

    const { handler, options } = handlerEntry;

    // Apply preventDefault/stopPropagation based on options
    if (options?.preventDefault ?? this.globalPreventDefault) {
      event.preventDefault();
    }
    if (options?.stopPropagation ?? this.globalStopPropagation) {
      event.stopPropagation();
    }

    if (this.debug) {
      console.log(`[EventManager] Handling ${eventType}:${action}`, actionElement.dataset);
    }

    // Execute handler
    try {
      const result = handler(event, actionElement);
      if (result instanceof Promise) {
        result.catch((error) => {
          console.error(`[EventManager] Async handler error for ${action}:`, error);
        });
      }
    } catch (error) {
      console.error(`[EventManager] Handler error for ${action}:`, error);
    }

    // Remove handler if once option is set
    if (options?.once) {
      this.off(eventType, action);
    }
  }

  /**
   * Register an event handler for a specific action.
   *
   * @param eventType - The DOM event type (click, change, etc.)
   * @param action - The action name (matches data-action attribute)
   * @param handler - The handler function
   * @param options - Optional handler options
   *
   * @example
   * eventManager.on('click', 'start-verification', (e, el) => {
   *   handleStartVerification();
   * });
   */
  on(
    eventType: SupportedEventType,
    action: string,
    handler: EventHandler,
    options?: HandlerEntry['options']
  ): this {
    // Ensure event type is attached
    this.attachEventType(eventType);

    if (!this.handlers.has(eventType)) {
      this.handlers.set(eventType, new Map());
    }

    this.handlers.get(eventType)!.set(action, { handler, options });

    if (this.debug) {
      console.log(`[EventManager] Registered handler for ${eventType}:${action}`);
    }

    return this;
  }

  /**
   * Unregister an event handler.
   */
  off(eventType: SupportedEventType, action: string): this {
    this.handlers.get(eventType)?.delete(action);

    if (this.debug) {
      console.log(`[EventManager] Unregistered handler for ${eventType}:${action}`);
    }

    return this;
  }

  /**
   * Register a one-time event handler.
   */
  once(eventType: SupportedEventType, action: string, handler: EventHandler): this {
    return this.on(eventType, action, handler, { once: true });
  }

  /**
   * Check if a handler is registered for an action.
   */
  has(eventType: SupportedEventType, action: string): boolean {
    return this.handlers.get(eventType)?.has(action) ?? false;
  }

  /**
   * Get all registered actions for an event type.
   */
  getActions(eventType: SupportedEventType): string[] {
    return Array.from(this.handlers.get(eventType)?.keys() ?? []);
  }

  /**
   * Clear all handlers for an event type, or all handlers if no type specified.
   */
  clear(eventType?: SupportedEventType): void {
    if (eventType) {
      this.handlers.get(eventType)?.clear();
    } else {
      this.handlers.forEach((map) => map.clear());
    }

    if (this.debug) {
      console.log(`[EventManager] Cleared handlers${eventType ? ` for ${eventType}` : ''}`);
    }
  }

  /**
   * Programmatically trigger an action.
   * Useful for testing or keyboard shortcuts.
   */
  trigger(eventType: SupportedEventType, action: string, element?: HTMLElement): void {
    const handlerEntry = this.handlers.get(eventType)?.get(action);
    if (!handlerEntry) {
      if (this.debug) {
        console.warn(`[EventManager] No handler to trigger for ${eventType}:${action}`);
      }
      return;
    }

    // Create a synthetic event
    const syntheticEvent = new Event(eventType, { bubbles: true, cancelable: true });

    // Use provided element or create a dummy one
    const targetElement =
      element ?? (document.querySelector(`[data-action="${action}"]`) as HTMLElement);

    if (targetElement) {
      handlerEntry.handler(syntheticEvent, targetElement);
    }
  }

  /**
   * Destroy the event manager and remove all listeners.
   */
  destroy(): void {
    this.attachedEvents.forEach((eventType) => {
      this.root.removeEventListener(eventType, this.boundHandleEvent, true);
    });
    this.attachedEvents.clear();
    this.handlers.clear();

    if (this.debug) {
      console.log('[EventManager] Destroyed');
    }
  }
}

/**
 * Singleton instance for global event management.
 */
let globalEventManager: EventManager | null = null;

/**
 * Get or create the global event manager instance.
 */
export function getEventManager(config?: EventManagerConfig): EventManager {
  if (!globalEventManager) {
    globalEventManager = new EventManager(config);
  }
  return globalEventManager;
}

/**
 * Initialize the global event manager.
 * Should be called after DOM is ready.
 */
export function initEventManager(config?: EventManagerConfig): EventManager {
  globalEventManager = new EventManager(config);
  globalEventManager.init();
  return globalEventManager;
}

/**
 * Helper to extract common data attributes from an element.
 */
export function getActionData(element: HTMLElement): Record<string, string> {
  const data: Record<string, string> = {};
  for (const key of Object.keys(element.dataset)) {
    if (key !== 'action') {
      data[key] = element.dataset[key]!;
    }
  }
  return data;
}
